import {useState, useEffect, useCallback} from 'react';
import _ from 'lodash';
import {Table, Tag, Input, Form, Button, Avatar} from 'antd';
import {UserOutlined} from '@ant-design/icons';
import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import moment from 'moment';
import {GET_DABA_POWER_USER_INVESTMENTS} from '../resolvers';
import styles from './dabaPower.module.scss';
import UserLink from 'components/userLink/UserLink';

export const InvestmentsTab = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [minInvestments, setMinInvestments] = useState(0);
  const [debouncedMinInvestments, setDebouncedMinInvestments] = useState(0);

  const debouncedSetSearch = useCallback(
    _.debounce(value => setDebouncedSearch(value), 400),
    [],
  );
  const debouncedSetMinInvestments = useCallback(
    _.debounce(value => setDebouncedMinInvestments(value), 400),
    [],
  );

  useEffect(() => {
    debouncedSetSearch(search);
    return () => debouncedSetSearch.cancel?.();
  }, [search]);
  useEffect(() => {
    debouncedSetMinInvestments(minInvestments);
    return () => debouncedSetMinInvestments.cancel?.();
  }, [minInvestments]);

  const {data, loading, refetch} = useQuery(GET_DABA_POWER_USER_INVESTMENTS, {
    variables: {
      filters: {
        search: debouncedSearch || undefined,
        minInvestments:
          debouncedMinInvestments > 0 ? debouncedMinInvestments : undefined,
        paginate: true,
        page: currentPage,
        limit,
      },
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error?.message),
  });

  useEffect(() => {
    setCurrentPage(1);
    refetch({
      filters: {
        search: debouncedSearch || undefined,
        minInvestments:
          debouncedMinInvestments > 0 ? debouncedMinInvestments : undefined,
        paginate: true,
        page: 1,
        limit,
      },
    });
  }, [debouncedSearch, debouncedMinInvestments]);

  const handleReset = () => {
    setSearch('');
    setDebouncedSearch('');
    setMinInvestments(0);
    setDebouncedMinInvestments(0);
    setCurrentPage(1);
    debouncedSetSearch.cancel?.();
    debouncedSetMinInvestments.cancel?.();
    refetch({
      filters: {
        paginate: true,
        page: 1,
        limit,
      },
    });
  };

  const handleTableChange = pagination => {
    setCurrentPage(pagination.current);
    setLimit(pagination.pageSize);
    refetch({
      filters: {
        search: debouncedSearch || undefined,
        minInvestments:
          debouncedMinInvestments > 0 ? debouncedMinInvestments : undefined,
        paginate: true,
        page: pagination.current,
        limit: pagination.pageSize,
      },
    });
  };

  const response = data?.getDabaPowerUserInvestments;
  const dataSource =
    response?.__typename === 'ResponseWithDabaPowerUserInvestments'
      ? response?.data || []
      : [];
  const paginationData = response?.pagination || {};

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => {
        const name = [record?.user?.firstName, record?.user?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim();
        return (
          <div className={styles.userCell}>
            <Avatar src={record?.user?.imageUrl} icon={<UserOutlined />} />
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <span className={styles.userName}>
                <UserLink userId={record?.user?.id}>
                  {name || record?.user?.email || record?.user?.id || '—'}
                </UserLink>
              </span>
              <span className={styles.userEmail}>
                {record?.user?.email || '—'}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Location',
      key: 'location',
      dataIndex: 'user',
      render: val =>
        val?.country
          ? `${val?.city ? `${val?.city}, ` : ''}${val?.country}`.trim()
          : '—',
    },
    {
      title: 'Plan',
      dataIndex: 'isProUser',
      key: 'isProUser',
      render: isPro => (
        <Tag color={isPro ? 'purple' : 'default'}>{isPro ? 'Pro' : 'Free'}</Tag>
      ),
    },
    {
      title: 'Total Investment Count',
      dataIndex: 'totalInvestments',
      key: 'totalInvestments',
    },
    {
      title: 'Last Investment',
      dataIndex: 'lastInvestment',
      key: 'lastInvestment',
      render: date =>
        date ? moment(date).format('DD MMM, YYYY hh:mm a') : '—',
    },
  ];

  return (
    <div className={styles.membersTableContainer}>
      <div className={styles.membersHeader}>
        <h3 className={styles.sectionTitle}>Daba Power Users - Investments</h3>
      </div>
      <div className={styles.filterSection}>
        <p className={styles.filterDescription}>
          Filter users by number of investments.
        </p>
        <div className={styles.filterRow}>
          <Form.Item className={styles.filterField}>
            <Input
              placeholder="Search by username..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={styles.searchInput}
              allowClear
            />
          </Form.Item>
          <Form.Item
            label="Min. Investment Count"
            className={styles.filterField}>
            <Input
              type="number"
              min={0}
              value={minInvestments}
              onChange={e => setMinInvestments(Number(e.target.value) || 0)}
              placeholder="0"
              className={styles.minInvestmentsInput}
            />
          </Form.Item>
          <Button className={styles.resetButton} onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>
      <div className={styles.paginationRow}>
        Showing {dataSource.length} of {paginationData.total ?? 0} users
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        rowKey={record => record?.user?.id || Math.random()}
        pagination={{
          current: currentPage,
          pageSize: limit,
          total: paginationData.total || 0,
          showSizeChanger: true,
          position: ['bottomRight'],
        }}
        onChange={handleTableChange}
        className="antd-table"
      />
    </div>
  );
};
